import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Select } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import isEmail from 'validator/es/lib/isEmail';

import { SaveIcon } from '~/img';
import { Form } from '~/components';
import { phoneValidation } from '~/utils';
import { Business } from '~/types';
import { editContacts } from '~/api/businesses';
import PhoneInput from '~/components/PhoneInput';

type Contact = {
  channel: 'EMAIL' | 'PHONE' | 'WEBSITE' | 'TELEGRAM' | 'WECHAT' | 'WHATSAPP';
  id: string;
};

interface Props {
  record: Business | undefined;
  refetch: () => void;
}

const AddContact: FC<Props> = (props) => {
  const { record, refetch } = props;
  const [t] = useTranslation('profile');
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOk = () => {
    form.submit();
  };

  const onFinish = async (values: Contact) => {
    if (!record) return;
    setLoading(true);
    try {
      await editContacts(record.id, {
        contacts: (record.contacts || []).concat([values]),
      });
      refetch();
      handleClose();
      void message.success(t('contactAdded'));
    } catch (error: any) {
      void message.error(error.response?.data?.message);
    }
    setLoading(false);
  };

  return (
    <>
      <Button type="primary" onClick={handleOpen}>
        {t('addContactDetails')}
      </Button>
      <Modal
        title={t('addContactDetails')}
        destroyOnClose
        visible={open}
        onCancel={handleClose}
        cancelText={t('translation:cancel')}
        onOk={handleOk}
        okText={t('translation:save')}
        okButtonProps={{
          icon: <SaveIcon />,
        }}
        confirmLoading={loading}
      >
        <Form form={form} onFinish={onFinish}>
          <Form.Item name="channel" rules={[{ required: true }]}>
            <Select
              placeholder={t('channel')}
              options={[
                { value: 'EMAIL', label: t('EMAIL') },
                { value: 'PHONE', label: t('PHONE') },
                { value: 'TELEGRAM', label: t('TELEGRAM') },
                { value: 'WHATSAPP', label: t('WHATSAPP') },
                { value: 'WECHAT', label: t('WECHAT') },
                { value: 'WEBSITE', label: t('WEBSITE') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="id"
            dependencies={['channel']}
            rules={[
              ({ getFieldValue }) => ({
                // eslint-disable-next-line consistent-return
                validator(rule, value, callback) {
                  // eslint-disable-next-line prefer-promise-reject-errors
                  if (!value) return Promise.reject(t('translation:required'));
                  if (getFieldValue('channel') === 'PHONE' && phoneValidation) {
                    return phoneValidation(rule, value, callback);
                  }
                  if (getFieldValue('channel') === 'EMAIL') {
                    if (!isEmail(value)) return Promise.reject(t('translation:notEmail'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <PhoneInput placeholder={t('value')} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddContact;
